import { Dispatch, Fragment, isValidElement, SetStateAction, useEffect, useState } from 'react';
import { Typography } from '../typography/typography';
import { QuipliLink } from '../link/quipli-link';
import classNames from 'classnames';
import { Checkbox } from '../checkbox/checkbox';
import { DataTableColumn, DataTableProps, DataTableRow, DataTableSort } from './data-table';
import { Paginator } from '../paginator/paginator-v2';
import { Spinner } from '../spinner/spinner';
import { PaginationRequest } from '../../clients/quipli-service/pagination/pagination';

export const SortIcon = ({ currentDirection }: { currentDirection?: 'asc' | 'desc' }) => (
  <div className="relative text-xs h-[20px] max-h-[20px] text-grey-300">
    <i className={classNames('fa fa-caret-up absolute top-0', { '!text-grey-500': currentDirection === 'asc' })} />
    <i
      className={classNames('fa fa-caret-down absolute bottom-0', { '!text-grey-500': currentDirection === 'desc' })}
    />
  </div>
);

export const DesktopDataTable = <TPaginator extends PaginationRequest>({
  data,
  columnNames,
  title,
  tabs,
  noDataText = 'No data available',
  isLoading,
  pagination,
  request,
  setRequest,
  cypressPrevious,
  cypressNext,
  wrapperClassNames = '',
  onCheckBoxSelect,
  checkBoxActions,
  sort,
  ...props
}: DataTableProps<TPaginator>) => {
  const [selectedItems, setSelectedItems] = useState<Array<number>>([]);

  useEffect(() => {
    setSelectedItems([]);
  }, [pagination, data]);

  useEffect(() => {
    onCheckBoxSelect &&
      onCheckBoxSelect(
        (data.filter((elem, idx) => selectedItems.includes(idx)) || []).map((elem: DataTableRow) => elem?.data),
      );
  }, [selectedItems]);

  return (
    <>
      {title && <h4 className={`mb-6 font-light capitalize text-gray-700 ${title.className}`}>{title.text}</h4>}

      <div className="relative w-full">
        {selectedItems.length > 0 && (
          <div className="table-checkbox-actions rounded-[12px]">
            <div className="flex pl-2">
              <Checkbox
                type="white"
                isChecked={true}
                onChange={() => setSelectedItems([])}
                icon={
                  <svg width="12" height="2" viewBox="0 0 12 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="12" height="2" rx="1" fill="#1D2939" />
                  </svg>
                }
              />
              <Typography
                type="text-md"
                className="text-[white]"
              >{`${selectedItems.length} items selected`}</Typography>
            </div>

            <div className="pr-2">{checkBoxActions && checkBoxActions}</div>
            <div className="table-checkbox-actions-vector">
              <svg width="20" height="8" viewBox="0 0 20 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10 8C7 8 4.0001 -1.39876e-06 6.99382e-07 -1.74846e-06L20 0C16.0251 -3.47497e-07 13 8 10 8Z"
                  fill="#1D2939"
                />
              </svg>
            </div>
          </div>
        )}
      </div>

      <div
        className={classNames(
          // overflow-x-scroll
          'data-table overflow-x-auto bg-white rounded-t-[12px]',
          wrapperClassNames,
          {
            'rounded-b-[12px]': !pagination,
          },
        )}
      >
        {tabs && tabs}

        <table {...props}>
          <thead>
            <DataTableHeader
              data={data}
              columnNames={columnNames}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
              sort={sort}
            />
          </thead>
          <tbody>
            {data.length === 0 || (isLoading !== undefined && isLoading) ? (
              <tr>
                <td colSpan={columnNames.length} className="text-center">
                  {isLoading ? (
                    <Spinner />
                  ) : (
                    <Typography type="text-sm" className="text-grey-500">
                      {noDataText}
                    </Typography>
                  )}
                </td>
              </tr>
            ) : (
              <DataTableBody
                columnNames={columnNames}
                data={data}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
              />
            )}
          </tbody>
        </table>
      </div>

      {setRequest && pagination && (
        <Paginator
          request={request}
          setRequest={(paginator) => setRequest((prev) => ({ ...prev, ...paginator }))}
          pagination={pagination}
          cypressPrevious={cypressPrevious}
          cypressNext={cypressNext}
        />
      )}
    </>
  );
};

const DataTableHeader = ({
  data,
  columnNames,
  selectedItems,
  setSelectedItems,
  sort,
}: {
  data: DataTableRow[];
  columnNames: DataTableColumn[];
  selectedItems: Array<number>;
  setSelectedItems: Dispatch<SetStateAction<number[]>>;
  sort?: DataTableSort;
}) => {
  return (
    <tr>
      {columnNames.map(
        ({
          key,
          label,
          fixedAsFirst,
          fixedAsLast,
          isCheckBoxColumn,
          hide = false,
          showRule = 'Both',
          className,
          sortable = false,
          sortField,
        }) => {
          return (
            !hide &&
            showRule !== 'OnlyMobile' && (
              <th
                key={key}
                className={classNames('text-left', `${key}-col`, className, {
                  'sticky-col-first': fixedAsFirst,
                  'sticky-col-last': fixedAsLast,
                })}
              >
                {isCheckBoxColumn ? (
                  !selectedItems.length && (
                    <Checkbox
                      type="white"
                      isChecked={selectedItems.length > 0 && selectedItems.length === data.length}
                      onChange={(isChecked) => {
                        if (isChecked) {
                          setSelectedItems(data.map((elem, idx) => idx));
                        } else {
                          setSelectedItems([]);
                        }
                      }}
                    />
                  )
                ) : (
                  <div
                    className={classNames('flex items-center gap-2', { 'cursor-pointer': sortable })}
                    onClick={() => {
                      if (!sortable || !sortField) return;

                      sort?.onSort(sortField, sort?.field === sortField && sort?.direction === 'desc' ? 'asc' : 'desc');
                    }}
                  >
                    <Typography
                      type="text-sm"
                      className="text-grey-500 flex"
                      weight={sortable && sortField === sort?.field ? 'semi-bold' : 'regular'}
                    >
                      {label !== undefined ? label : key}
                    </Typography>

                    {sortable && (
                      <SortIcon currentDirection={sort?.field === sortField ? sort?.direction : undefined} />
                    )}
                  </div>
                )}
              </th>
            )
          );
        },
      )}
    </tr>
  );
};

const DataTableBody = ({
  data,
  columnNames,
  selectedItems,
  setSelectedItems,
}: {
  data: DataTableRow[];
  columnNames: DataTableColumn[];
  selectedItems: Array<number>;
  setSelectedItems: Dispatch<SetStateAction<number[]>>;
}) => {
  return (
    <>
      {data.map(({ fields, className, redirectTo, extraRow, key }, idx) => {
        return (
          <Fragment key={`tr-wrapper-${key || idx}`}>
            <tr className={classNames(className, { '!border-b-0 [&>td]:!border-b-0': !!extraRow })}>
              {columnNames.map(
                ({ key, fixedAsFirst, fixedAsLast, isCheckBoxColumn, hide = false, showRule = 'Both', className }) => {
                  let tdContent = (
                    <>
                      {isCheckBoxColumn && (
                        <Checkbox
                          type="white"
                          isChecked={selectedItems.includes(idx)}
                          onChange={(isChecked) => {
                            if (isChecked) {
                              setSelectedItems((prevState: number[]) => [...prevState, idx]);
                            } else {
                              setSelectedItems((prevState: number[]) =>
                                prevState.filter((elem: number) => elem !== idx),
                              );
                            }
                          }}
                        />
                      )}

                      {isValidElement(fields[key]) ? (
                        fields[key]
                      ) : (
                        <Typography type="text-sm" className="text-quipli-black-800">
                          {fields[key]}
                        </Typography>
                      )}
                    </>
                  );

                  if (redirectTo && !fixedAsFirst && !fixedAsLast) {
                    tdContent = <QuipliLink to={redirectTo}>{tdContent}</QuipliLink>;
                  }

                  const td = !hide && showRule !== 'OnlyMobile' && (
                    <td
                      key={`td-${idx}-${key}`}
                      className={classNames(key, className, {
                        hasCheckBox: isCheckBoxColumn,
                        'sticky-col-first': fixedAsFirst,
                        'sticky-col-last': fixedAsLast,
                      })}
                    >
                      {tdContent}
                    </td>
                  );

                  return td;
                },
              )}
            </tr>
            {!!extraRow && (
              <tr>
                <td colSpan={columnNames.length} className={classNames(extraRow.className)}>
                  {extraRow.content}
                </td>
              </tr>
            )}
          </Fragment>
        );
      })}
    </>
  );
};
